import type { CanvasEntity } from '../entityTypes';

declare class ImageCapture {
  constructor(track: MediaStreamTrack);
  grabFrame(): Promise<ImageBitmap>;
}

/** Map entity kind -> data attribute name for DOM lookup */
const ENTITY_DATA_ATTR: Record<string, string> = {
  task: 'data-canvas-node',
  sprite: 'data-canvas-sprite',
  textblock: 'data-canvas-textblock',
  textlabel: 'data-canvas-textlabel',
  waypoint: 'data-canvas-waypoint',
  group: 'data-canvas-group',
};

function findEntityElement(container: HTMLElement, entity: CanvasEntity): HTMLElement | null {
  const attr = ENTITY_DATA_ATTR[entity.kind];
  if (!attr) return null;
  return container.querySelector(`[${attr}="${entity.id}"]`) as HTMLElement | null;
}

/**
 * Capture the currently selected Canvas entities as a PNG image
 * and copy it to the clipboard via getDisplayMedia + grabFrame.
 */
export async function captureSelectionAsImage(params: {
  selectedEntities: CanvasEntity[];
  containerEl: HTMLElement;
  panZoom: { panX: number; panY: number; zoom: number };
}): Promise<void> {
  const { selectedEntities, containerEl } = params;

  const posEntities = selectedEntities.filter(e => e.kind !== 'connector');
  if (posEntities.length === 0) return;

  // 1. Start screen capture FIRST (user confirms sharing dialog)
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,
  } as any);

  const track = stream.getVideoTracks()[0];
  let overlayStyle: HTMLStyleElement | null = null;

  try {
    const imageCapture = new ImageCapture(track);

    // 2. Wait for UI to settle (Chrome's "sharing this tab" bar shifts content)
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    // 3. Compute bounding box from ACTUAL DOM positions (after sharing started)
    const PADDING = 16;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const entity of posEntities) {
      const el = findEntityElement(containerEl, entity);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      minX = Math.min(minX, r.left);
      minY = Math.min(minY, r.top);
      maxX = Math.max(maxX, r.right);
      maxY = Math.max(maxY, r.bottom);
    }

    if (!isFinite(minX)) { track.stop(); return; }

    minX -= PADDING;
    minY -= PADDING;
    maxX += PADDING;
    maxY += PADDING;

    // Clip to viewport
    minX = Math.max(minX, 0);
    minY = Math.max(minY, 0);
    maxX = Math.min(maxX, window.innerWidth);
    maxY = Math.min(maxY, window.innerHeight);

    if (minX >= maxX || minY >= maxY) { track.stop(); return; }

    // 4. Hide selection overlays & decorations
    overlayStyle = document.createElement('style');
    overlayStyle.textContent = `
      [data-capture-hide] {
        display: none !important;
      }
      [data-canvas-entity] {
        box-shadow: none !important;
        outline: none !important;
      }
      [data-canvas-group] {
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(overlayStyle);

    // Double rAF to ensure the repaint is flushed
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    // 5. Grab a single frame
    const bitmap = await imageCapture.grabFrame();

    // 6. Restore overlays & stop stream
    document.head.removeChild(overlayStyle);
    overlayStyle = null;
    track.stop();

    // 7. Crop: viewport coords -> bitmap coords
    const scaleX = bitmap.width / window.innerWidth;
    const scaleY = bitmap.height / window.innerHeight;
    const cropX = Math.round(minX * scaleX);
    const cropY = Math.round(minY * scaleY);
    const cropW = Math.round((maxX - minX) * scaleX);
    const cropH = Math.round((maxY - minY) * scaleY);

    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    bitmap.close();

    // 8. Copy PNG to clipboard
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
    );

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  } finally {
    if (overlayStyle?.parentNode) {
      document.head.removeChild(overlayStyle);
    }
    if (track.readyState === 'live') {
      track.stop();
    }
  }
}
